import { loadBaseCSS } from '../shared/button-utils.js';
import { upgradeProperty } from '../shared/form-control.js';
import { primitiveEvent, primitiveStyles, rovingIndex } from '../shared/primitive-utils.js';
import '../disclosure/qds-disclosure.js';
import type { QdsDisclosure, QdsDisclosureToggleDetail } from '../disclosure/qds-disclosure.js';

/**
 * Coordinates direct disclosure children. Every enabled heading remains in the tab sequence.
 * @element qds-accordion
 * @attr {boolean} multiple - Allow more than one expanded item. Default false.
 * @property {boolean} multiple - Multiple expansion mode.
 * @property {string[]} values - Expanded item identities; empty closes all panels.
 * @slot - Direct qds-disclosure children with unique value attributes.
 * @fires {{ component: HTMLElement; values: string[] }} qds-change - User expansion changed. Detail: { component, values }.
 */
export class QdsAccordion extends HTMLElement {
  static get observedAttributes(): string[] { return ['multiple']; }
  private _observer: MutationObserver;
  private _previous = new Set<QdsDisclosure>();
  private _pending: string[] | null = null;
  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).innerHTML = `<style>${primitiveStyles}:host { display: grid; gap: 8px; }</style><slot></slot>`;
    this._observer = new MutationObserver(() => this._sync());
    loadBaseCSS();
  }
  connectedCallback(): void {
    for (const prop of ['multiple', 'values']) upgradeProperty(this, prop);
    this.addEventListener('qds-toggle', this._onToggle as EventListener);
    this.addEventListener('keydown', this._onKeydown);
    this._observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['expanded', 'value'] });
    if (this._pending) { const values = this._pending; this._pending = null; this.values = values; }
    this._sync();
  }
  disconnectedCallback(): void {
    this._observer.disconnect();
    this.removeEventListener('qds-toggle', this._onToggle as EventListener);
    this.removeEventListener('keydown', this._onKeydown);
  }
  attributeChangedCallback(): void { if (this.isConnected) this._sync(); }
  get multiple(): boolean { return this.hasAttribute('multiple'); }
  set multiple(value: boolean) { this.toggleAttribute('multiple', !!value); }
  get values(): string[] { return this._items().filter(item => item.expanded).map(item => this._key(item)); }
  set values(values: string[]) {
    const safe = Array.isArray(values) ? values.map(String) : [];
    if (!this.isConnected) { this._pending = safe; return; }
    const allowed = this.multiple ? safe : safe.slice(0, 1);
    this._items().forEach(item => { item.expanded = allowed.includes(this._key(item)); });
    this._sync();
  }
  private _items(): QdsDisclosure[] { return Array.from(this.children).filter((child): child is QdsDisclosure => child.localName === 'qds-disclosure'); }
  private _key(item: QdsDisclosure): string { return item.value || String(this._items().indexOf(item)); }
  private _sync(): void {
    const expanded = this._items().filter(item => item.expanded);
    if (!this.multiple && expanded.length > 1) {
      const selected = expanded.find(item => !this._previous.has(item)) || expanded[0];
      expanded.forEach(item => { if (item !== selected) item.expanded = false; });
    }
    this._previous = new Set(this._items().filter(item => item.expanded));
  }
  private _onToggle = (event: CustomEvent<QdsDisclosureToggleDetail>): void => {
    if (!this._items().includes(event.detail.component)) return;
    if (event.detail.expanded && !this.multiple) this._items().forEach(item => { if (item !== event.detail.component) item.expanded = false; });
    this._sync();
    primitiveEvent(this, 'qds-change', { component: this, values: this.values });
  };
  private _onKeydown = (event: KeyboardEvent): void => {
    const items = this._items().filter(item => !item.disabled);
    const item = items.find(candidate => event.composedPath()[0] === candidate.shadowRoot?.querySelector('[part="trigger"]'));
    if (!item || event.altKey || event.ctrlKey || event.metaKey) return;
    const index = items.indexOf(item);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 :
      event.key === 'ArrowDown' ? rovingIndex(index, items.length, 1) : event.key === 'ArrowUp' ? rovingIndex(index, items.length, -1) : -1;
    if (next < 0) return;
    event.preventDefault(); items[next].focus();
  };
}
if (!customElements.get('qds-accordion')) customElements.define('qds-accordion', QdsAccordion);
